import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Fuel, Car, Calendar, User, TrendingUp, FileText, Search, X, BarChart3 } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend,
} from "recharts";

const ro_days = ["Duminică", "Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă"];

function monthOpts() {
  const out = [{ value: "", label: "Toate lunile" }];
  const now = new Date();
  for (let i = 0; i < 24; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString("ro-RO", { year: "numeric", month: "long" }),
    });
  }
  return out;
}

export default function ChipAlimentare() {
  const [chips, setChips] = useState(null);
  const [cars, setCars] = useState([]);
  const [search, setSearch] = useState("");
  const [carFilter, setCarFilter] = useState(null); // { car_id, label }
  const [showCarsList, setShowCarsList] = useState(false);
  const [openChip, setOpenChip] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => {
    api.get("/fuel-chips").then((r) => setChips(r.data || [])).catch(() => {
      toast.error("Eroare la încărcarea chipurilor");
      setChips([]);
    });
    api.get("/fuel-chips-cars").then((r) => setCars(r.data || [])).catch(() => setCars([]));
  };
  useEffect(() => { load(); }, []);

  // Deep-link support: /chip-alimentare?chip=1234
  useEffect(() => {
    const q = searchParams.get("chip");
    if (q && chips) {
      const match = chips.find((c) => c.chip_no === q);
      if (match) setOpenChip(q);
    }
  }, [searchParams, chips]);

  const filteredCars = useMemo(() => {
    if (!cars) return [];
    const q = search.trim().toLowerCase();
    if (!q) return cars;
    return cars.filter((c) => {
      return [c.intern_number, c.numar_inmatriculare, c.marca, c.model, c.label]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
        || (c.chips || []).some((ch) => String(ch).toLowerCase().includes(q));
    });
  }, [cars, search]);

  const filtered = useMemo(() => {
    if (!chips) return [];
    const q = search.trim().toLowerCase();
    let list = chips;
    if (carFilter) {
      list = list.filter((c) => (c.cars || []).some((x) => x.id === carFilter.car_id));
    } else if (q) {
      list = list.filter((c) => {
        // Match on chip_no OR any associated car field
        if ((c.chip_no || "").toLowerCase().includes(q)) return true;
        return (c.cars || []).some((car) => [car.intern_number, car.numar_inmatriculare, car.marca, car.model, car.label]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)));
      });
    }
    return list;
  }, [chips, search, carFilter]);

  const totals = useMemo(() => {
    if (!chips) return { liters: 0, fills: 0 };
    return {
      liters: chips.reduce((s, c) => s + (c.total_liters || 0), 0),
      fills: chips.reduce((s, c) => s + (c.total_fills || 0), 0),
    };
  }, [chips]);

  return (
    <div className="space-y-6" data-testid="chip-alimentare-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <span className="w-11 h-11 rounded-md bg-amber-50 text-amber-700 border border-amber-200 grid place-items-center">
              <Fuel size={22}/>
            </span>
            Chip Alimentare
          </h1>
          <p className="text-sm text-zinc-500 mt-2">Urmărește utilizarea fiecărui chip din modulul Combustibil.</p>
        </div>
        <div className="flex items-center gap-5 text-sm">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Total chipuri</div>
            <div className="font-display text-2xl font-bold">{chips?.length ?? "…"}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Litri alimentați</div>
            <div className="font-display text-2xl font-bold text-amber-700">{totals.liters.toFixed(0)} L</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Alimentări</div>
            <div className="font-display text-2xl font-bold">{totals.fills}</div>
          </div>
        </div>
      </header>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-400"/>
            <Input
              data-testid="chip-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută după chip, nr. intern, înmatriculare, marcă…"
              className="pl-8 h-9 text-sm"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCarsList((v) => !v)}
            data-testid="chip-toggle-cars-list"
            className={showCarsList ? "border-amber-500 text-amber-700" : ""}
          >
            <Car size={14}/> {showCarsList ? "Ascunde autoturisme" : "Arată autoturisme"}
          </Button>
          {carFilter && (
            <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 border border-amber-200 px-2 py-1 rounded">
              Filtru: <b className="ml-1">{carFilter.label}</b>
              <button
                type="button"
                onClick={() => setCarFilter(null)}
                className="ml-1 hover:text-red-700"
                data-testid="chip-clear-car-filter"
                aria-label="Elimină filtrul"
              >
                <X size={12}/>
              </button>
            </span>
          )}
        </div>

        {showCarsList && (
          <div className="card-flat p-3" data-testid="chip-cars-list">
            <div className="text-[11px] uppercase tracking-wider text-zinc-600 font-bold mb-2 flex items-center gap-2">
              <Car size={13}/> Autoturisme cu alimentări
              <span className="text-zinc-400 normal-case tracking-normal font-normal">({filteredCars.length}/{cars.length})</span>
            </div>
            {filteredCars.length === 0 ? (
              <div className="text-sm text-zinc-400 italic py-3 text-center">
                {cars.length === 0 ? "Nicio alimentare înregistrată pe un autoturism." : "Niciun autoturism nu corespunde căutării."}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                {filteredCars.map((c) => {
                  const active = carFilter?.car_id === c.car_id;
                  return (
                    <button
                      key={c.car_id}
                      type="button"
                      onClick={() => setCarFilter(active ? null : { car_id: c.car_id, label: c.label })}
                      data-testid={`chip-car-item-${c.car_id}`}
                      className={`text-left border rounded-md p-2 transition-all bg-white hover:shadow-sm ${
                        active ? "border-amber-500 bg-amber-50" : "border-zinc-200 hover:border-amber-400"
                      }`}
                      title={c.label}
                    >
                      <div className="flex items-start gap-2">
                        <Car size={14} className={active ? "text-amber-700 mt-0.5" : "text-zinc-500 mt-0.5"}/>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold truncate">
                            {c.intern_number && <span className="mr-1.5 text-zinc-700">Auto {c.intern_number}</span>}
                            {c.numar_inmatriculare || "—"}
                          </div>
                          <div className="text-[10px] text-zinc-500 truncate">
                            {[c.marca, c.model].filter(Boolean).join(" ") || "—"}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(c.chips || []).map((ch) => (
                              <span key={ch} className="text-[9px] font-mono font-bold bg-amber-100 text-amber-800 px-1 py-0.5 rounded">
                                #{ch}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] text-zinc-500">Litri</div>
                          <div className="text-xs font-bold text-amber-700 tabular-nums">{c.total_liters.toFixed(0)}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {chips === null ? (
        <div className="text-sm text-zinc-400 italic text-center py-10">Se încarcă chipurile…</div>
      ) : filtered.length === 0 ? (
        <div className="card-flat p-10 text-center text-sm text-zinc-500">
          {chips.length === 0 ? "Nu există alimentări cu chip înregistrate." : `Niciun chip pentru filtrele curente.`}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {filtered.map((c) => (
            <button
              key={c.chip_no}
              type="button"
              onClick={() => { setOpenChip(c.chip_no); setSearchParams({ chip: c.chip_no }); }}
              className="card-flat p-5 text-left hover:border-amber-500 hover:shadow-md transition-all group"
              data-testid={`chip-card-${c.chip_no}`}
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-md bg-amber-50 text-amber-700 border border-amber-200 grid place-items-center group-hover:scale-[1.05] transition-transform">
                  <Fuel size={22}/>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">Chip</span>
              </div>
              <div className="font-display text-xl font-bold mt-3 truncate" title={c.chip_no}>
                {c.chip_no}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">Total litri</div>
                  <div className="font-bold text-amber-700 tabular-nums">{c.total_liters.toFixed(2)} L</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">Alimentări</div>
                  <div className="font-bold tabular-nums">{c.total_fills}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">Ultima</div>
                  <div className="text-xs text-zinc-700">{c.last_use || "—"}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">Autoturisme</div>
                  <div className="text-xs text-zinc-700">{c.cars_count}</div>
                </div>
              </div>
              {(c.cars && c.cars.length > 0) && (
                <div className="mt-3 pt-2 border-t border-zinc-100 flex flex-wrap gap-1">
                  {c.cars.slice(0, 3).map((car) => (
                    <span key={car.id} className="text-[9px] bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded truncate max-w-[120px]" title={car.label}>
                      {car.intern_number ? `#${car.intern_number}` : ""} {car.numar_inmatriculare || car.label}
                    </span>
                  ))}
                  {c.cars.length > 3 && (
                    <span className="text-[9px] text-zinc-400">+{c.cars.length - 3}</span>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {openChip && (
        <ChipDetailDialog
          chipNo={openChip}
          onClose={() => { setOpenChip(null); setSearchParams({}); }}
        />
      )}
    </div>
  );
}

function ChipDetailDialog({ chipNo, onClose }) {
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chart, setChart] = useState(null); // null | "bar" | "line"
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (day) qs.set("day", day);
    else if (month) qs.set("month", month);
    api.get(`/fuel-chips/${encodeURIComponent(chipNo)}?${qs.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Eroare"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [chipNo, month, day]); // eslint-disable-line

  const generatePdf = async () => {
    setGeneratingPdf(true);
    try {
      const qs = new URLSearchParams();
      if (day) qs.set("day", day);
      else if (month) qs.set("month", month);
      const r = await api.post(`/fuel-chips/${encodeURIComponent(chipNo)}/pdf?${qs.toString()}`);
      const fid = r.data?.pdf_file_id;
      if (fid) {
        window.open(fileUrl(fid, { download: true }), "_blank");
        toast.success("PDF generat cu succes");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la generarea PDF-ului");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const totals = data?.totals || {};
  const entries = data?.entries || [];
  const daily = data?.daily || [];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="chip-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Fuel size={20} className="text-amber-700"/>
            Chip #{chipNo}
            <span className="text-xs font-normal text-zinc-500 ml-2">
              {totals.total_fills || 0} alimentări · {Number(totals.total_liters || 0).toFixed(2)} L · {totals.unique_cars || 0} autoturisme
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 p-3 rounded-md bg-zinc-50 border border-zinc-200">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-600 font-bold">Luna</Label>
            <select
              data-testid="chip-filter-month"
              value={month}
              onChange={(e) => { setMonth(e.target.value); setDay(""); }}
              className="mt-1 h-9 rounded-md border border-zinc-300 px-2 text-sm bg-white min-w-[180px]"
            >
              {monthOpts().map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-600 font-bold">Ziua (exact)</Label>
            <Input
              data-testid="chip-filter-day"
              type="date"
              value={day}
              onChange={(e) => { setDay(e.target.value); setMonth(""); }}
              className="mt-1 h-9 w-40"
            />
          </div>
          {(month || day) && (
            <Button variant="outline" size="sm" onClick={() => { setMonth(""); setDay(""); }} data-testid="chip-filter-reset">
              <X size={14}/> Reset
            </Button>
          )}
          <div className="ml-auto flex flex-wrap gap-2">
            <Button
              data-testid="chip-show-chart"
              variant="outline"
              onClick={() => setChart((c) => c === "bar" ? null : "bar")}
              className={chart === "bar" ? "border-amber-500 text-amber-700" : ""}
            >
              <BarChart3 size={15}/> {chart === "bar" ? "Ascunde grafic" : "Grafic animat"}
            </Button>
            <Button
              data-testid="chip-generate-pdf"
              onClick={generatePdf}
              disabled={generatingPdf || !entries.length}
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)]"
            >
              <FileText size={15}/> {generatingPdf ? "Se generează…" : "PDF"}
            </Button>
          </div>
        </div>

        {/* Totals cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          <Card label="Total litri" value={`${Number(totals.total_liters || 0).toFixed(2)} L`} accent="#D97706"/>
          <Card label="Alimentări" value={totals.total_fills || 0}/>
          <Card label="Autoturisme" value={totals.unique_cars || 0}/>
          <Card label="Persoane" value={totals.unique_persons || 0}/>
        </div>

        {/* Chart (animated) */}
        {chart === "bar" && daily.length > 0 && (
          <div className="mt-4 card-flat p-4" data-testid="chip-chart-container">
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-bold mb-2 flex items-center gap-2">
              <TrendingUp size={14}/> Litri &amp; alimentări pe zi
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={daily} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }}/>
                  <YAxis yAxisId="L" tick={{ fontSize: 10 }} label={{ value: "L", position: "insideLeft", fontSize: 10 }}/>
                  <YAxis yAxisId="C" orientation="right" tick={{ fontSize: 10 }} label={{ value: "#", position: "insideRight", fontSize: 10 }}/>
                  <RTooltip/>
                  <Legend/>
                  <Bar yAxisId="L" dataKey="liters" name="Litri" fill="#D97706" animationDuration={1100} animationEasing="ease-out" radius={[3, 3, 0, 0]}/>
                  <Bar yAxisId="C" dataKey="count" name="Alimentări" fill="#3B82F6" animationDuration={1100} animationBegin={250} animationEasing="ease-out" radius={[3, 3, 0, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {daily.length > 1 && (
              <div className="mt-2" style={{ width: "100%", height: 180 }}>
                <ResponsiveContainer>
                  <LineChart data={daily} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }}/>
                    <YAxis tick={{ fontSize: 10 }}/>
                    <RTooltip/>
                    <Line type="monotone" dataKey="liters" name="Litri" stroke="#D97706" strokeWidth={2} dot={{ r: 3 }} animationDuration={1400}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Entries table */}
        <div className="mt-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-bold mb-2">Alimentări înregistrate</div>
          {loading ? (
            <div className="text-sm text-zinc-400 italic py-8 text-center">Se încarcă…</div>
          ) : entries.length === 0 ? (
            <div className="text-sm text-zinc-400 italic py-8 text-center">Nicio alimentare pentru acest filtru.</div>
          ) : (
            <div className="card-flat p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 sticky top-0">
                  <tr className="text-left">
                    <th className="p-2 text-xs font-semibold text-zinc-500 uppercase"><Calendar size={11} className="inline mr-1"/>Data</th>
                    <th className="p-2 text-xs font-semibold text-zinc-500 uppercase">Zi</th>
                    <th className="p-2 text-xs font-semibold text-zinc-500 uppercase"><Car size={11} className="inline mr-1"/>Autoturism</th>
                    <th className="p-2 text-xs font-semibold text-zinc-500 uppercase"><User size={11} className="inline mr-1"/>Persoana</th>
                    <th className="p-2 text-xs font-semibold text-zinc-500 uppercase text-right">Litri</th>
                    <th className="p-2 text-xs font-semibold text-zinc-500 uppercase text-right">km</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-t border-zinc-100 row-hover" data-testid={`chip-entry-${e.id}`}>
                      <td className="p-2 font-mono text-xs">{e.data_alimentare}</td>
                      <td className="p-2 text-xs text-zinc-600">{e.day_of_week || "—"}</td>
                      <td className="p-2">{e.car_label || "—"}</td>
                      <td className="p-2">{e.persoana || "—"}</td>
                      <td className="p-2 text-right tabular-nums font-bold text-amber-700">{Number(e.litri || 0).toFixed(2)}</td>
                      <td className="p-2 text-right tabular-nums text-zinc-600">{Math.round(e.numar_km || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="chip-close">Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Card({ label, value, accent = "#111" }) {
  return (
    <div className="card-flat p-3 text-center">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{label}</div>
      <div className="font-display text-xl font-extrabold" style={{ color: accent }}>{value}</div>
    </div>
  );
}
